import { clsx } from 'clsx'
import {
  type ComponentPropsWithoutRef,
  type FC,
  type SubmitEventHandler,
  useRef,
} from 'react'
import { useIsClient } from 'usehooks-ts'

import {
  cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier,
  cartIntermediateAccessorGetSellerData,
  useProductAddToCart,
} from '../../../../../cart-intermediate'
import {
  useOpenableControls,
  useRerenderAndExecute,
} from '../../../../../generic-hooks'
import { gtmDataLayerPushHomeOneAddToCart } from '../../../../../gtm'
import { ProductPageRxByPrincipleDialog } from '../../../../../product'
import { productAccessorGetVariantCode } from '../../../../model/productAccessor/productAccessorGetVariantCode'
import { ProductAddToCartAppRedemption } from '../../../ProductAppRedemption/ProductAddToCartAppRedemption'
import { useProductAppRedemptionEligibility } from '../../../ProductAppRedemption/useProductAppRedemptionEligibility'
import { useProductPageContext } from '../../../productPageContext'
import {
  type ProductPrescriptionDrawerControls,
  useProductPrescriptionDrawerControls,
} from '../../../ProductPrescriptionDrawer/useProductPrescriptionDrawerControls'
import { useProductPageUdpTrackingAddToCart } from '../../hooks/useProductPageUdpTrackingAddToCart'
import { ProductPagePrivatePrescriptionNotification } from '../ProductPagePrivatePrescriptionNotification/ProductPagePrivatePrescriptionNotification'

import { ProductAddToCartButton } from './ProductAddToCartButton'
import { ProductAddToCartPrescriptionRedemption } from './ProductAddToCartPrescriptionRedemption'
import { useBuildAddToCartParams } from './useBuildAddToCartParams'
import { useProductAddToCartCtaShouldShowSelectErxModal } from './useProductAddToCartCtaShouldShowSelectErxModal'

const PRODUCT_ADD_TO_CART_FORM_ID = 'product-add-to-cart-form-id'

type ProductAddToCartCtaProps = Omit<
  ComponentPropsWithoutRef<'form'>,
  'action' | 'id' | 'onSubmit'
>

const useFormSubmitHandler = (params: {
  attributeSelectionControls: ReturnType<
    typeof useProductPageContext
  >['attributeSelectionControls']
  handleAddToCart: () => Promise<void>
  handleUdpTrackingAddToCart: () => void
  prescriptionRedemptionControls: ProductPrescriptionDrawerControls
  rxByPrincipleControls: ReturnType<
    typeof useProductPageContext
  >['rxByPrincipleControls']
  rxByPrincipleDialogControls: ReturnType<typeof useOpenableControls>
  shouldOpenPrescriptionRedemption: boolean
}): SubmitEventHandler<HTMLFormElement> => {
  const {
    attributeSelectionControls,
    handleAddToCart,
    handleUdpTrackingAddToCart,
    prescriptionRedemptionControls,
    rxByPrincipleControls,
    rxByPrincipleDialogControls,
    shouldOpenPrescriptionRedemption,
  } = params

  const onSubmit: SubmitEventHandler<HTMLFormElement> = (event): void => {
    if (
      !attributeSelectionControls.attributeNotSelectedErrorControls.validateWhenAddingToCart()
    ) {
      event.preventDefault()

      return
    }
    if (shouldOpenPrescriptionRedemption) {
      event.preventDefault()
      prescriptionRedemptionControls.handleOpen()

      return
    }
    if (
      rxByPrincipleControls.isReasonRequired &&
      !rxByPrincipleControls.selectedReason
    ) {
      event.preventDefault()
      rxByPrincipleDialogControls.handleOpen()

      return
    }
    event.preventDefault()

    void handleAddToCart()

    gtmDataLayerPushHomeOneAddToCart()
    handleUdpTrackingAddToCart()
  }

  return onSubmit
}

export const ProductAddToCartCta: FC<ProductAddToCartCtaProps> = ({
  className,
  ...props
}) => {
  const formRef = useRef<HTMLFormElement>(null)

  const {
    attributeSelectionControls,
    currentOffer,
    currentPrescriptionType,
    product,
    quantitySelectionControls: { quantity },
    rxByPrincipleControls,
  } = useProductPageContext()
  const shouldOpenPrescriptionRedemption =
    useProductAddToCartCtaShouldShowSelectErxModal()
  const showAppRedemption = useProductAppRedemptionEligibility()
  const prescriptionRedemptionControls = useProductPrescriptionDrawerControls()
  const { addToCartParams, isPrivatePrescriptionAndCartMetadataHasErx } =
    useBuildAddToCartParams()
  const rxByPrincipleDialogControls = useOpenableControls()
  const rerenderAndExecute = useRerenderAndExecute()

  const redpoints = currentOffer
    ? cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier(
        currentOffer,
      )
    : 0

  const { handleAddToCart, isMutating } = useProductAddToCart({
    currentPrescriptionType,
    offerId: currentOffer?.offerId,
    product,
    quantity,
    redpoints,
    rxByPrincipleSelectedReason: rxByPrincipleControls.isReasonRequired
      ? rxByPrincipleControls.selectedReason
      : undefined,
    sellerData: cartIntermediateAccessorGetSellerData(currentOffer),
    source: 'pdp-buy-box',
  })
  const handleUdpTrackingAddToCart = useProductPageUdpTrackingAddToCart()

  const pageLoaded = useIsClient()

  const onSubmit = useFormSubmitHandler({
    attributeSelectionControls,
    handleAddToCart,
    handleUdpTrackingAddToCart,
    prescriptionRedemptionControls,
    rxByPrincipleControls,
    rxByPrincipleDialogControls,
    shouldOpenPrescriptionRedemption,
  })

  return (
    <>
      <form
        {...props}
        className={clsx(className, showAppRedemption && 'tablet:col-span-2')}
        id={PRODUCT_ADD_TO_CART_FORM_ID}
        onSubmit={onSubmit}
        ref={formRef}
      >
        {Object.entries(
          (addToCartParams as unknown as Record<string, string> | undefined) ??
            {},
        ).map(([name, value]) => {
          return <input key={name} name={name} type="hidden" value={value} />
        })}
        {showAppRedemption ? (
          <ProductAddToCartAppRedemption loading={isMutating || !pageLoaded} />
        ) : (
          <ProductAddToCartButton loading={isMutating || !pageLoaded} />
        )}
        {isPrivatePrescriptionAndCartMetadataHasErx ? (
          <ProductPagePrivatePrescriptionNotification />
        ) : null}
      </form>
      <ProductAddToCartPrescriptionRedemption
        controls={prescriptionRedemptionControls}
      />
      {rxByPrincipleControls.isReasonRequired ? (
        <ProductPageRxByPrincipleDialog
          autoOpenOnLocationHash
          key={productAccessorGetVariantCode(product)}
          onClose={rxByPrincipleDialogControls.handleClose}
          onOpen={rxByPrincipleDialogControls.handleOpen}
          onReasonSelect={(reason) => {
            rxByPrincipleDialogControls.handleClose()
            rxByPrincipleControls.setSelectedReason(reason)
            rerenderAndExecute(() => {
              formRef.current?.requestSubmit()
            })
          }}
          open={rxByPrincipleDialogControls.isOpen}
          productName={product.name}
          reasonSet={rxByPrincipleControls.reasonSet}
        />
      ) : null}
    </>
  )
}
